import { useState, useEffect } from 'react'
import { Shield, ShieldCheck, ShieldAlert, AlertTriangle, Eye, EyeOff, Monitor } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useChangePassword, useDeleteAccount } from '@/hooks'

interface SecuritySectionProps {
  onLogout?: () => void
}

export function SecuritySection({ onLogout }: SecuritySectionProps) {
  const { changePassword, loading: changingPassword, error: changePasswordError } = useChangePassword()
  const { deleteAccount, loading: deletingAccount, error: deleteAccountError } = useDeleteAccount()

  // Change password form state
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  // Password strength
  const [passwordStrength, setPasswordStrength] = useState<'weak' | 'medium' | 'strong' | null>(null)

  // Delete account state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deletePassword, setDeletePassword] = useState('')
  const [showDeletePassword, setShowDeletePassword] = useState(false)

  // Toast notifications
  const [showSuccess, setShowSuccess] = useState(false)
  const [showError, setShowError] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  // Active sessions (mock data - would come from API)
  const [activeSessions] = useState([
    {
      id: '1',
      device: 'Chrome on Windows',
      ip: '192.168.1.1',
      location: 'New York, USA',
      lastActive: new Date().toISOString(),
      current: true
    }
  ])

  // Calculate password strength
  useEffect(() => {
    if (!newPassword) {
      setPasswordStrength(null)
      return
    }

    let strength = 0
    if (newPassword.length >= 8) strength++
    if (newPassword.length >= 12) strength++
    if (/[a-z]/.test(newPassword)) strength++
    if (/[A-Z]/.test(newPassword)) strength++
    if (/[0-9]/.test(newPassword)) strength++
    if (/[^a-zA-Z0-9]/.test(newPassword)) strength++

    if (strength <= 2) setPasswordStrength('weak')
    else if (strength <= 4) setPasswordStrength('medium')
    else setPasswordStrength('strong')
  }, [newPassword])

  const showSuccessToast = (message: string) => {
    setSuccessMessage(message)
    setShowSuccess(true)
    setTimeout(() => setShowSuccess(false), 3000)
  }

  const showErrorToast = (message: string) => {
    setErrorMessage(message)
    setShowError(true)
    setTimeout(() => setShowError(false), 3000)
  }

  const handleChangePassword = async () => {
    // Validation: all fields required
    if (!currentPassword || !newPassword || !confirmPassword) {
      showErrorToast('Please fill in all password fields')
      return
    }

    // Validation: new password and confirm must match
    if (newPassword !== confirmPassword) {
      showErrorToast('New passwords do not match')
      return
    }

    // Validation: password strength
    if (passwordStrength === 'weak') {
      showErrorToast('Password is too weak. Please use a stronger password.')
      return
    }

    // Validation: new password must be different from current
    if (currentPassword === newPassword) {
      showErrorToast('New password must be different from current password')
      return
    }

    // Call API
    const result = await changePassword({
      currentPassword,
      newPassword,
      confirmPassword
    })

    if (result) {
      showSuccessToast('Password changed successfully!')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } else {
      showErrorToast(changePasswordError || 'Failed to change password. Please try again.')
    }
  }

  const handleDeleteAccount = async () => {
    // Validation: password required
    if (!deletePassword) {
      showErrorToast('Please enter your password to confirm account deletion')
      return
    }

    // Call API
    const result = await deleteAccount({ password: deletePassword })

    if (result) {
      showSuccessToast('Account deleted successfully')
      setShowDeleteDialog(false)

      // Logout after short delay
      setTimeout(() => {
        if (onLogout) {
          onLogout()
        } else {
          // Clear localStorage and redirect
          localStorage.clear()
          window.location.href = '/login'
        }
      }, 1500)
    } else {
      showErrorToast(deleteAccountError || 'Failed to delete account. Please try again.')
    }
  }

  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 'weak': return 'bg-red-500'
      case 'medium': return 'bg-yellow-500'
      case 'strong': return 'bg-green-500'
      default: return 'bg-gray-300'
    }
  }

  const getPasswordStrengthText = () => {
    switch (passwordStrength) {
      case 'weak': return 'Weak'
      case 'medium': return 'Medium'
      case 'strong': return 'Strong'
      default: return ''
    }
  }

  return (
    <div className="space-y-6">
      {/* Success Toast */}
      {showSuccess && (
        <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-right duration-300">
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md shadow-lg p-4 flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-green-600 dark:text-green-400" />
            <span className="text-sm text-green-800 dark:text-green-200 font-medium">
              {successMessage}
            </span>
          </div>
        </div>
      )}

      {/* Error Toast */}
      {showError && (
        <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-right duration-300">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md shadow-lg p-4 flex items-center gap-3">
            <ShieldAlert className="w-5 h-5 text-red-600 dark:text-red-400" />
            <span className="text-sm text-red-800 dark:text-red-200 font-medium">
              {errorMessage}
            </span>
          </div>
        </div>
      )}

      {/* Change Password Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="w-5 h-5 text-primary" />
          <h2 className="text-xl font-semibold">Change Password</h2>
        </div>

        <div className="space-y-4">
          {/* Current Password */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Current Password
            </label>
            <div className="relative">
              <Input
                type={showCurrentPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                disabled={changingPassword}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* New Password */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              New Password
            </label>
            <div className="relative">
              <Input
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                disabled={changingPassword}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            {/* Password Strength Indicator */}
            {newPassword && (
              <div className="mt-2">
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-200 dark:bg-gray-700 h-2 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                      style={{ width: passwordStrength === 'weak' ? '33%' : passwordStrength === 'medium' ? '66%' : '100%' }}
                    />
                  </div>
                  <span className="text-xs text-muted-foreground">{getPasswordStrengthText()}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Use 8+ characters with mix of letters, numbers & symbols
                </p>
              </div>
            )}
          </div>

          {/* Confirm New Password */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Confirm New Password
            </label>
            <div className="relative">
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                disabled={changingPassword}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {confirmPassword && newPassword !== confirmPassword && (
              <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
            )}
          </div>
        </div>

        <div className="mt-6">
          <Button
            onClick={handleChangePassword}
            variant="default"
            disabled={changingPassword}
          >
            {changingPassword ? 'Changing Password...' : 'Change Password'}
          </Button>
        </div>
      </div>

      {/* Active Sessions Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <Monitor className="w-5 h-5 text-primary" />
          <h2 className="text-xl font-semibold">Active Sessions</h2>
        </div>

        <div className="space-y-3">
          {activeSessions.map((session) => (
            <div
              key={session.id}
              className={`p-4 rounded-lg border ${
                session.current
                  ? 'border-primary bg-primary/5'
                  : 'border-border'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{session.device}</p>
                    {session.current && (
                      <span className="text-xs px-2 py-1 bg-primary text-primary-foreground rounded-full">
                        Current
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {session.location} • {session.ip}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Last active: {new Date(session.lastActive).toLocaleString()}
                  </p>
                </div>
                {!session.current && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {/* TODO: Implement revoke session */}}
                  >
                    Revoke
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Delete Account Section */}
      <div className="bg-card border border-destructive/50 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <AlertTriangle className="w-5 h-5 text-destructive" />
          <h2 className="text-xl font-semibold text-destructive">Delete Account</h2>
        </div>

        <p className="text-sm text-muted-foreground mb-4">
          Once you delete your account, there is no going back. Please be certain.
        </p>

        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4 mb-4">
          <p className="text-sm text-destructive font-medium">This action cannot be undone.</p>
          <ul className="text-xs text-destructive/80 mt-2 space-y-1 list-disc list-inside">
            <li>Your profile and personal data will be permanently deleted</li>
            <li>Your account will be removed from the platform</li>
            <li>All your activity and content will be deleted</li>
          </ul>
        </div>

        <Button
          onClick={() => setShowDeleteDialog(true)}
          variant="destructive"
        >
          Delete Account
        </Button>
      </div>

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-5 h-5" />
              Delete Account
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete your account? This action is permanent and cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div className="bg-gray-50 dark:bg-gray-800 rounded-md p-4 space-y-3">
            <p className="text-sm font-medium">This will permanently delete:</p>
            <ul className="text-sm space-y-1 list-disc list-inside text-muted-foreground">
              <li>Your profile and personal information</li>
              <li>Your account and all associated data</li>
              <li>Your activity history and content</li>
              <li>All access to the platform</li>
            </ul>
            <p className="text-xs text-muted-foreground mt-2">
              Your account will be deleted immediately and you will be logged out.
            </p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium">
              Enter your password to confirm
            </label>
            <div className="relative">
              <Input
                type={showDeletePassword ? 'text' : 'password'}
                value={deletePassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDeletePassword(e.target.value)}
                placeholder="Enter your password"
                disabled={deletingAccount}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowDeletePassword(!showDeletePassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showDeletePassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <DialogFooter>
            <Button
              onClick={() => {
                setShowDeleteDialog(false)
                setDeletePassword('')
              }}
              variant="outline"
              disabled={deletingAccount}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteAccount}
              variant="destructive"
              disabled={deletingAccount || !deletePassword}
            >
              {deletingAccount ? 'Deleting...' : 'Delete Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
